import "./PricingView.css";
import {
    DealerAdjustment,
    NonDiscountOption,
    Assembly,
    PilotInspectionFee,
    PricingOption, IncentiveProgramInfo, ApprovalRequest, ApproverRole
} from "../../api/models";
import Utils from "../../util/util";
import {Button, ButtonProps, Dropdown, notification} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import React, { useContext } from "react";
import PricingOptionButtonModal, {PricingOptionCustomOption} from "./PricingOptionButtonModal";
import { useQuoteContext } from "../../contexts/QuoteContext";
import { ConfiguratorContext } from "../../context";
import { ItemType } from "antd/es/menu/interface";
import PilotInspectionFeeButtonModal from "./PilotInspectionModal";
import {SaveDto} from "../../api";
import {useIntl} from "react-intl";
import {FORM_PERCENT_DISCOUNT} from "../../pages/configurator";
import PercentDiscountModal from "./PercentDiscountModal";
import IncentiveProgramSelect from "./IncentiveProgramSelect";

export type DealerAdjustmentsButtonModalProps =  Omit<ButtonProps, "value" | "onChange"> & {
  value?:DealerAdjustment
  onChange?: (v: DealerAdjustment | undefined) => void
};
const DealerAdjustmentsButtonModal = (props: DealerAdjustmentsButtonModalProps) => {

  const predefinedOptions = [
      "FET", "State Taxes", "Reg Fees", "Interest", "Freight", "Warranty", "Training", "Dealer Commission", "Penalty", PricingOptionCustomOption
  ]

  return <PricingOptionButtonModal {...props} 
    title={"Dealer Adjustments"}
    optionsLst={predefinedOptions}
  />
}


export type NonDiscountOptionsButtonModalProps =  Omit<ButtonProps, "value" | "onChange"> & {
  value?:NonDiscountOption
  onChange?: (v: NonDiscountOption | undefined) => void
};
const NonDiscountOptionsButtonModal = (props:NonDiscountOptionsButtonModalProps) => {

  const predefinedOptions = [
    PricingOptionCustomOption
  ];


  const configurator = useContext(ConfiguratorContext);
  const allowNegative = configurator.isAdmin() || configurator.isFinance() || configurator.isSalesDesk();

  return <PricingOptionButtonModal {...props} 
    title={"Non-Discount Options"}
    optionsLst={predefinedOptions}
    allowNegative={allowNegative}
  />
}


const NOT_FOUND = -1;
const PricingView = (props: {
    onChange?: (q?:SaveDto | undefined ) => void
    disabled?: boolean
} ) => {
    const { disabled } = props;

    const intl = useIntl();
    const configurator = useContext(ConfiguratorContext);
    const { quote, adminView } = useQuoteContext();
    const pricingDetails = quote?.pricingBreakdown;

    const isSalesDesk = configurator.isSalesDesk();
    const isBmRepresentative = configurator.isBmRepresentative(quote);

    const approvers = new Set(quote?.latestApproval?.approvalStep.approvers);
    const {
        isReadOnly,
        isPending,
        readOnlyMsg,
    } = Utils.getQuoteState(configurator, quote, false);

    const isQuoteApprovalProcessStep = isPending && quote?.latestApproval?.approvalType === ApprovalRequest.QUOTE_APPROVAL &&
        !(approvers.has(ApproverRole.SALES_DESK) && isSalesDesk) &&
        !(approvers.has(ApproverRole.BM_REPRESENTATIVE) && isBmRepresentative); // Sales desk and BM Rep can modify quote in approval step including concession

    const showDetails = adminView;

    if ( !pricingDetails ) return <></>;

    const isPercentDiscountNonZero = pricingDetails.percentDiscount != 0.0;
    const dealerPercentDiscount =  isPercentDiscountNonZero 
        ? -1 * pricingDetails.dealerNet * pricingDetails.percentDiscount / 100
        : 0;
    const isDealerDiscountedNetDiff = pricingDetails.dealerDiscountedNet != pricingDetails.dealerNet;
    const dealerDiscount = -1  * ( pricingDetails.dealerMsrp - pricingDetails.dealerNet );

    const updatePricingOptionLst = (lst:PricingOption[] | undefined,  existing:PricingOption | undefined, replacement:PricingOption | undefined) : PricingOption[] | undefined => {
        const ndx = lst?.findIndex(po => po.key === existing?.key ) ?? -1;
        if ( ndx !== NOT_FOUND ) {
            lst?.splice( ndx, 1 );
        }

        if ( replacement ) {
            if (!lst) return [replacement];
            lst.push( replacement );
        }

        lst?.sort( (a,b) => (a.label || "").localeCompare(b.label || "") );

        return lst;
    }

    const handleChangeDealerAdjustments = ( existing?:DealerAdjustment | undefined) => {
        return async (replacement?:DealerAdjustment | undefined) => {
            const lst = quote?.dealerAdjustmentList && [...quote?.dealerAdjustmentList];
            const update = updatePricingOptionLst(lst, existing, replacement);

            props.onChange?.({
                dealerAdjustmentList: update
            });
        }
    }

    const handleChangeNonDiscountOptions = ( existing?:NonDiscountOption | undefined) => {
        return async (replacement?:NonDiscountOption | undefined) => {
            const lst = quote?.nonDiscountOptionList && [...quote?.nonDiscountOptionList];
            const update = updatePricingOptionLst(lst, existing, replacement);

            props.onChange?.({
                nonDiscountOptionList: update
            });
        }
    }

    const handleChangePilotInspectionFee= async ( pilotInspectionFee?:PilotInspectionFee | undefined) => {
        props.onChange?.({
            pilotInspectionFee: pilotInspectionFee ?? null
        });
    }

    const handleUpdateDiscountPercent = (discountPercent:number) => {
        props.onChange?.({
            [FORM_PERCENT_DISCOUNT]: discountPercent
        })
    }

    const handleIncentiveProgramChange = (lst: IncentiveProgramInfo[] | undefined) => {
        const incentivePrograms = lst?.map(i => i.id ) || [];
        props.onChange?.({incentivePrograms});
    }

    const getDefaultDisabledMsg = () => {
        return quote?.archived ? "Archived quote cannot be modified."
            : isReadOnly ? readOnlyMsg
                : undefined;
    }

    const getIncentiveProgramDisabledMsg = () => {
        return isQuoteApprovalProcessStep ? "Incentive Programs selection is disabled during the approval step."
            : getDefaultDisabledMsg();
    }


    const getHeaderFontSize = (txt:string|undefined) => {
      const HEADER_LINE_LENGTH = 30;
      const txtLength = txt?.length || 0;

      if (txtLength > ( HEADER_LINE_LENGTH * 2 ) ) return "7pt";
      if (txtLength > HEADER_LINE_LENGTH ) return "9pt"

      return "inherit";
    }

    const hasIncentiveProgram = !!quote.incentivePrograms?.length;

    const dropdownItems:ItemType[] = []
    dropdownItems.push( {
      key: "da",
      label: <DealerAdjustmentsButtonModal style={{border: "none"}}
        onChange={handleChangeDealerAdjustments()} />
    });

    if (!isReadOnly ) {
      dropdownItems.push( {
        key: "ndo",
        label: <NonDiscountOptionsButtonModal style={{border: "none"}}
          onChange={handleChangeNonDiscountOptions()}
        />
      });

      dropdownItems.push( {
          key: "pilotInspection",
          label: <PilotInspectionFeeButtonModal style={{border: "none"}} quantity={quote?.quantity || 0}
                                                onChange={handleChangePilotInspectionFee} />
      });

      dropdownItems.push( {
          key: "incentiveProgram",
          label: <IncentiveProgramSelect style={{border: "none"}}
                                         modelId={quote?.model.id}
                                         disabled={!!getIncentiveProgramDisabledMsg()}
                                         onDisabledClick={() => Utils.notifyDisabled(getIncentiveProgramDisabledMsg())}
                                         onChange={handleIncentiveProgramChange} />
      });


    }

    return <>
        <div className={"pricingView"}>
          <h3>Pricing Detail
            {!disabled &&
              <div style={{float:"right"}}>
                <Dropdown trigger={["click"]}
                  menu={{items:dropdownItems}}
                >
                  <Button data-testid="pricing-view-add-btn" shape="circle" type="primary" icon={<PlusOutlined />} size="small" />
                </Dropdown>
              </div>}
          </h3>

            <table className="basePrices">
                <tbody>
                    {showDetails && (
                        <>
                            <tr>
                                <th>Material Cost:</th>
                                <td>{Utils.formatMoney( pricingDetails.materialPrice ) }</td>
                            </tr>
                            <tr>
                                <th>Labor Cost:</th>
                                <td>{Utils.formatMoney( pricingDetails.laborPrice ) }</td>
                            </tr>
                            <tr>
                                <th>Inbound Freight:</th>
                                <td>{Utils.formatMoney( pricingDetails.inboundFreight ) }</td>
                            </tr>
                            <tr>
                                <th>Tariffs Surcharges:</th>
                                <td>{Utils.formatMoney( pricingDetails.tariffsSurcharges ) }</td>
                            </tr>
                            <tr>
                                <th>PDI:</th>
                                <td>{Utils.formatMoney( pricingDetails.pdi ) }</td>
                            </tr>
                            <tr>
                                <th>Warranty:</th>
                                <td>{Utils.formatMoney( pricingDetails.warranty ) }</td>
                            </tr>
                            <tr>
                                <th>Gross Margin:</th>
                                <td>{Utils.formatMoney( pricingDetails.grossMargin ) }</td>
                            </tr>
                            {(pricingDetails.priceMatch != 0)  && <tr>
                              <th>Price Match:</th>
                                <td>{Utils.formatMoney( pricingDetails.priceMatch) }</td>
                            </tr>}
                        </>
                    ) }
                    {pricingDetails && pricingDetails.customOptionsMsrp >= 0 && <tr>
                        <th>Custom Option(s) MSRP:</th>
                        <td>{Utils.formatMoney( pricingDetails.customOptionsMsrp ) }</td>
                    </tr>}
                    <tr>
                        <th>Dealer MSRP:</th>
                        <td>{Utils.formatMoney( pricingDetails.dealerMsrp ) }</td>
                    </tr>
                    <tr>
                        <th>Dealer Discount:</th>
                        <td>{Utils.formatMoney( dealerDiscount ) }</td>
                    </tr>
                    <tr>
                        <th>Dealer Net:</th>
                        <td>{Utils.formatMoney( pricingDetails.dealerNet ) }</td>
                    </tr>

                    <tr>
                        <th>
                            <PercentDiscountModal style={{border: "none", padding: 0}}
                                                  value={pricingDetails.percentDiscount}
                                                  disabled={isReadOnly}
                                                  onChange={handleUpdateDiscountPercent}
                                                  label={
                                                      <div style={{
                                                          textAlign: "right",
                                                          borderBottom: !isReadOnly ? "1px solid black" : undefined,
                                                          color: "rgba(0, 0, 0, 0.88)",
                                                          fontSize: "14px",
                                                          fontWeight: "bold",
                                                      }}>
                                                          {Utils.formatPercent(quote?.percentDiscount ? quote.percentDiscount / 100 : 0)} Discount:
                                                      </div>}
                            />
                        </th>
                        <td>{Utils.formatMoney( dealerPercentDiscount ) }</td>
                    </tr>

                    {pricingDetails.nonDiscountedNetItems.map(
                        (item: Assembly ) => (
                            <tr key={item.id}>
                              <th style={{fontSize: getHeaderFontSize(item.label)}}>{item.label}:</th>
                                <td>{Utils.formatMoney( item.standardMaterialCost||0.0 ) }</td>
                            </tr>
                        )
                        )}

                    {showDetails && (
                            <tr>
                                <th>Commission Cost:</th>
                                <td>{ Utils.formatMoney( pricingDetails.commissionCost ) }</td>
                            </tr>
                    )}

                </tbody>
            </table>

          {!!(quote?.nonDiscountOptionList.length || quote?.pilotInspectionFee ) && <table className="basePrices" key={`ndo-table`}>
            <tbody>
            <tr>
              <th >Non-Discount Options</th>
              <th ></th>
            </tr>
              {quote.nonDiscountOptionList?.map( (ndo, ndx) => <React.Fragment key={`ndo-node-${ndx}`}>
              <tr>
                <th>
                  <NonDiscountOptionsButtonModal value={ndo} disabled={isReadOnly || disabled} style={{height: "auto"}} onChange={handleChangeNonDiscountOptions(ndo)} />
                </th>
                <td className="price">{Utils.formatMoney(ndo.value)}</td>
              </tr>
              {!!ndo.notes &&
              <tr>
                <td colSpan={2} style={{fontStyle:"italic"}} title={ndo.notes}>{ndo.notes}</td>
                </tr> }
              </React.Fragment>)}
            {Utils.coalesce(quote.pilotInspectionFee).map( (pif, ndx) => <React.Fragment key={`ndo-node-pilot-inspection-fee`}>
                <tr>
                    <th>
                        <PilotInspectionFeeButtonModal value={pif} quantity={quote.quantity || 0} disabled={isReadOnly || disabled} style={{height: "auto"}} onChange={handleChangePilotInspectionFee} />
                    </th>
                    <td className="price">{Utils.formatMoney(pif.value)}</td>
                </tr>
                {!!pif.notes &&
                  <tr>
                    <td colSpan={2} style={{fontStyle:"italic"}} title={pif.notes}>{pif.notes}</td>
                  </tr> }
            </React.Fragment>)}
            </tbody>
          </table>}


            <table className="basePrices">
                <tbody>

                    {isDealerDiscountedNetDiff && (
                        <tr>
                            <th >Net Price:</th>
                            <td>{Utils.formatMoney( pricingDetails.dealerDiscountedNet ) }</td>
                        </tr>
                    )}
                    {pricingDetails.nonDiscountedItems.map(
                        (item: Assembly) => (
                            <tr key={item.id}>
                                <th style={{fontSize:getHeaderFontSize(item.label) }}>{item.label}:</th>
                                <td>{Utils.formatMoney( item.standardMaterialCost||0.0 ) }</td>
                            </tr>
                        )
                        )}
                    {pricingDetails.incentivePrograms
                      .map( i => (
                            <tr key={i.name}>
                                <th style={{fontSize:getHeaderFontSize(i.name) }}>
                                    <IncentiveProgramSelect
                                        value={quote?.incentivePrograms}
                                        onChange={handleIncentiveProgramChange}
                                        modelId={quote?.model.id}
                                        disabled={!!getIncentiveProgramDisabledMsg()}
                                        onDisabledClick={() => Utils.notifyDisabled(getIncentiveProgramDisabledMsg())}
                                    />:
                                </th>
                                <td>{Utils.formatMoney( i.cost||0.0 ) }</td>
                            </tr>
                        )
                        )}
                    {pricingDetails.shippingDestination != null && (
                        <tr>
                            <th>Destination Charge:</th>
                            <td>{ Utils.formatMoney( pricingDetails.shippingDestination ) }</td>
                        </tr>
                    )}
                    {pricingDetails.customerTariffs != null && pricingDetails.customerTariffs > 0 && (
                        <tr>
                            <th>Import Tariffs:</th>
                            <td>{ Utils.formatMoney( pricingDetails.customerTariffs ) }</td>
                        </tr>
                    )}
                    {pricingDetails.supplementalCharges?.map(
                        (charge) => (
                            <tr key={charge.id}>
                                <th style={{fontSize: getHeaderFontSize(charge.label)}}>{charge.label}:</th>
                                <td>{Utils.formatMoney( charge.value ) }</td>
                            </tr>
                        )
                    )}
                  <tr className="summaryRow" >
                        <th>Dealer Price:</th>
                        <td>{ Utils.formatMoney( pricingDetails.dealerPrice ) }</td>
                    </tr>
                </tbody>
            </table>

          {!!quote?.dealerAdjustmentList.length && <table className="basePrices" key={`da-table`}>
            <tbody>
              <tr>
                <th >Dealer Adjustments</th>
                <th ></th>
              </tr>
              {quote.dealerAdjustmentList.map( (da, ndx) => <React.Fragment key={`da-node-${ndx}`}>
                <tr>
                  <th>
                    <DealerAdjustmentsButtonModal value={da} style={{height: "auto"}} onChange={handleChangeDealerAdjustments(da)} />
                  </th>
                  <td className="price">{Utils.formatMoney(da.value)}</td>
                </tr>
                {!!da.notes &&
                <tr>
                  <td colSpan={2} style={{fontStyle:"italic"}} title={da.notes}>{da.notes}</td>
                </tr> }
              </React.Fragment>)}
            </tbody>
          </table>}

            <table className="basePrices">
                <tbody>
                    <tr className="summaryRow">
                        {hasIncentiveProgram ? (
                            <>
                                <th>Customer Price:</th>
                                <td>{ Utils.formatMoney( pricingDetails.totalPrice ) }</td>
                            </>
                        ) : (
                            <>
                                <th>Total Price:</th>
                                <td>{ Utils.formatMoney( pricingDetails.totalPrice ) }</td>
                            </>
                        )}
                    </tr>
                </tbody>
            </table>
        </div>
    </>;


}


export default PricingView


